import React from 'react';
import { useIntl } from "react-intl";
import { useHistory, useRouteMatch } from "react-router-dom";
import "./BotVersionSelector.scss"
import {ChevronDown, Layers} from "lucide-react";
import {PostBot} from "../../../api/APIBot";
import {useQueryClient} from "react-query";
import {useCreateBotVersion} from "../hooks/useCreateBotVersion";
import BotSessionData from "../../../utils/botSessionStorage";

// Helper function to get the bot ID for a different version in the same group
export const getBotIdForVersion = (currentBotId, targetVersion, botData, currentBot) => {
    const bot = currentBot;

    // Guard clause: if no bot or versionGroup exists, return the current bot ID
    if (!bot || !bot?.config?.versionGroup) return currentBotId;

    const versionGroup = bot?.config?.versionGroup;

    // Find the bot ID that matches the target version in the same group
    for (const index in botData) {
        const otherBot = botData[index];
        // console.log(botData, index, versionGroup, targetVersion);

        if (otherBot?.config?.versionGroup === versionGroup &&
            otherBot?.config?.version === targetVersion) {
            return otherBot.id; // Return the bot's id property, not the array index
        }
    }

    // Return original if not found
    return currentBotId;
};

export const BotVersionChanger = ({activeVersion, handleVersionChange, versions, disabled = false}) => {
    const intl = useIntl()
    if (disabled) {
        return <div className={"bot-version"}>
            <button disabled className={"bot-version-button disabled"}>
                <Layers size={16} />
                {intl.formatMessage({
                    defaultMessage: "Version",
                    id: "version"
                })}:
                <b>{intl.formatMessage({
                    defaultMessage: "Default",
                    id: "default"
                })}</b>
                <ChevronDown size={20} />
            </button>
        </div>
    }

    return <div className={"bot-version"}>
        <button
            className={"bot-version-button"}
        >
            <Layers size={16} />
            {intl.formatMessage({
                defaultMessage: "Version",
                id: "version"
            })}:
            <b>{activeVersion === "DEFAULT" ?
                intl.formatMessage({
                    defaultMessage: "Default",
                    id: "default"
                }) : activeVersion
            }</b>
            <ChevronDown size={20} />
        </button>
        <select
            value={activeVersion}
            onChange={handleVersionChange}
            className={"bot-version-select"}
        >
            {versions.map((version, i) => {
                return <option value={version} key={i}>
                    {version === "DEFAULT" ?
                        intl.formatMessage({
                            defaultMessage: "Default",
                            id: "default"
                        }) : version
                    }
                </option>
            })}
            <option value="_create_new_version">
                + {intl.formatMessage({
                    id: 'bot.version.createNew',
                    defaultMessage: 'New version'
                })}
            </option>
        </select>
    </div>
}

export const BotVersionSelector = ({ botId, botData, currentBot }) => {
    const history = useHistory();
    const { createNewVersion } = useCreateBotVersion();

    // Skip rendering if there's no bot
    if (!currentBot) {
        return null;
    }

    const version = currentBot.config.version || "DEFAULT";
    let availableVersions = currentBot.config.versions || [version];

    // Check if current version has been renamed locally
    if (version !== "DEFAULT" && !availableVersions.includes(version)) {
        // Find the original name for this version
        const originalBot = botData?.find(bot => Number(bot.id) === Number(botId));
        const originalVersion = originalBot?.config?.version;

        // Replace the old version name with the new one in the dropdown
        if (originalVersion && availableVersions.includes(originalVersion)) {
            availableVersions = availableVersions.map(v =>
                v === originalVersion ? version : v
            );
        } else {
            availableVersions = [...availableVersions, version];
        }
    }

    const handleVersionChange = (e) => {
        const selectedOption = e.target.value;

        if (selectedOption === "_create_new_version") {
            createNewVersion(currentBot, botId);
            return;
        }

        const newBotId = getBotIdForVersion(botId, selectedOption, botData, currentBot);

        if (newBotId !== botId) {
            const currentPath = history.location.pathname;

            // Check if one of the specific tabs is in the URL
            let currentTab = '/information'; // Default tab

            if (currentPath.includes('/instructions')) {
                currentTab = '/instructions';
            } else if (currentPath.includes('/materials')) {
                currentTab = '/materials';
            } else if (currentPath.includes('/information')) {
                currentTab = '/information';
            }

            // Navigate to the same tab in the new bot version
            BotSessionData.clear(botId);
            history.push(`/support-bot/${newBotId}${currentTab}`);
        }
    };

    return (
        <BotVersionChanger
            activeVersion={version}
            handleVersionChange={handleVersionChange}
            versions={availableVersions}
            disabled={currentBot?.config?.versions?.length < 1}
        />
    );
};

export default BotVersionSelector;